/**
 * Lightweight syntax highlighter for HAR Insight
 * Supports JSON, XML, HTML, CSS, and JavaScript
 * Zero dependencies, ~2KB minified
 */

export type Language = 'json' | 'xml' | 'html' | 'css' | 'javascript' | 'text';

interface TokenRule {
  regex: RegExp;
  className: string;
}

const TOKEN_RULES: Record<Language, TokenRule[]> = {
  json: [
    { regex: /"(?:[^"\\]|\\.)*"(?=\s*:)/g, className: 'sh-property' },
    { regex: /"(?:[^"\\]|\\.)*"/g, className: 'sh-string' },
    { regex: /\b(?:true|false|null)\b/g, className: 'sh-keyword' },
    { regex: /-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g, className: 'sh-number' },
    { regex: /[{}[\]:,]/g, className: 'sh-punctuation' },
  ],
  xml: [
    { regex: /&lt;\?[\s\S]*?\?&gt;/g, className: 'sh-prolog' },
    { regex: /&lt;!--[\s\S]*?--&gt;/g, className: 'sh-comment' },
    { regex: /&lt;\/?[\w:-]+/g, className: 'sh-tag' },
    { regex: /\s[\w:-]+(?=\s*=)/g, className: 'sh-attr' },
    { regex: /"(?:[^"\\]|\\.)*"/g, className: 'sh-string' },
    { regex: /&gt;/g, className: 'sh-punctuation' },
  ],
  html: [
    { regex: /&lt;!--[\s\S]*?--&gt;/g, className: 'sh-comment' },
    { regex: /&lt;\/?[\w-]+/g, className: 'sh-tag' },
    { regex: /\s[\w-]+(?=\s*=)/g, className: 'sh-attr' },
    { regex: /"(?:[^"\\]|\\.)*"/g, className: 'sh-string' },
    { regex: /&gt;/g, className: 'sh-punctuation' },
    { regex: /&lt;|&gt;|&amp;/g, className: 'sh-entity' },
  ],
  css: [
    { regex: /\/\*[\s\S]*?\*\//g, className: 'sh-comment' },
    { regex: /@[\w-]+/g, className: 'sh-atrule' },
    { regex: /[.#]?[\w-]+(?=\s*\{)/g, className: 'sh-selector' },
    { regex: /[\w-]+(?=\s*:)/g, className: 'sh-property' },
    { regex: /:[\s]*[^;{}]+/g, className: 'sh-value' },
    { regex: /\b\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|s|ms|deg|rad)?\b/g, className: 'sh-number' },
    { regex: /[{}:;]/g, className: 'sh-punctuation' },
  ],
  javascript: [
    { regex: /\/\/[\s\S]*?$/gm, className: 'sh-comment' },
    { regex: /\/\*[\s\S]*?\*\//g, className: 'sh-comment' },
    { regex: /"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`/g, className: 'sh-string' },
    { regex: /\b(?:const|let|var|function|return|if|else|for|while|switch|case|break|continue|try|catch|finally|class|extends|import|export|from|async|await|new|this|typeof|instanceof|in|of)\b/g, className: 'sh-keyword' },
    { regex: /\b(?:true|false|null|undefined|NaN|Infinity)\b/g, className: 'sh-builtin' },
    { regex: /\b\d+(?:\.\d+)?\b/g, className: 'sh-number' },
    { regex: /[{}[\]()]/g, className: 'sh-punctuation' },
  ],
  text: [],
};

/**
 * Detect language from MIME type
 */
export function detectLanguage(mimeType: string): Language {
  if (!mimeType) return 'text';
  const normalized = mimeType.toLowerCase();
  
  if (normalized.includes('json')) return 'json';
  if (normalized.includes('xml') || normalized.includes('soap')) return 'xml';
  if (normalized.includes('html')) return 'html';
  if (normalized.includes('css')) return 'css';
  if (normalized.includes('javascript') || normalized.includes('ecmascript') || normalized.includes('js')) {
    return 'javascript';
  }
  
  return 'text';
}

/**
 * Escape HTML entities to prevent XSS
 */
function escapeHtml(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Safe decodeURIComponent that won't throw on invalid input
 */
export function safeDecodeURIComponent(value: string): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    // If decoding fails (e.g., malformed URI), return the original value
    return value;
  }
}

/**
 * Tokenize and highlight code
 */
export function highlight(code: string, language: Language): string {
  if (language === 'text' || !TOKEN_RULES[language]) {
    return escapeHtml(code);
  }

  const rules = TOKEN_RULES[language];
  const tokens: Array<{ text: string; className: string | null }> = [];
  
  // Find all token matches
  const matches: Array<{ start: number; end: number; className: string }> = [];
  
  for (const rule of rules) {
    rule.regex.lastIndex = 0;
    let match;
    while ((match = rule.regex.exec(code)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        className: rule.className,
      });
    }
  }
  
  // Sort matches by position and remove overlapping
  matches.sort((a, b) => a.start - b.start);
  const nonOverlapping: typeof matches = [];
  let lastEnd = 0;
  
  for (const match of matches) {
    if (match.start >= lastEnd) {
      nonOverlapping.push(match);
      lastEnd = match.end;
    }
  }
  
  // Build token list
  let pos = 0;
  for (const match of nonOverlapping) {
    if (match.start > pos) {
      tokens.push({ text: code.slice(pos, match.start), className: null });
    }
    tokens.push({ text: code.slice(match.start, match.end), className: match.className });
    pos = match.end;
  }
  
  if (pos < code.length) {
    tokens.push({ text: code.slice(pos), className: null });
  }
  
  // Render tokens
  return tokens.map(token => {
    const escaped = escapeHtml(token.text);
    return token.className ? `<span class="${token.className}">${escaped}</span>` : escaped;
  }).join('');
}

/**
 * CSS styles for syntax highlighting (light and dark mode)
 */
export const syntaxHighlightStyles = `
/* Syntax Highlighting Styles */
.sh-property { color: #7c3aed; font-weight: 500; }
.sh-string { color: #059669; }
.sh-keyword { color: #dc2626; font-weight: 500; }
.sh-builtin { color: #0891b2; }
.sh-number { color: #ea580c; }
.sh-punctuation { color: #6b7280; }
.sh-tag { color: #2563eb; font-weight: 500; }
.sh-attr { color: #7c3aed; }
.sh-comment { color: #6b7280; font-style: italic; }
.sh-selector { color: #7c3aed; font-weight: 500; }
.sh-value { color: #059669; }
.sh-atrule { color: #dc2626; }
.sh-prolog { color: #6b7280; }
.sh-entity { color: #0891b2; }

.dark .sh-property { color: #a78bfa; }
.dark .sh-string { color: #34d399; }
.dark .sh-keyword { color: #f87171; }
.dark .sh-builtin { color: #22d3ee; }
.dark .sh-number { color: #fb923c; }
.dark .sh-punctuation { color: #9ca3af; }
.dark .sh-tag { color: #60a5fa; }
.dark .sh-attr { color: #a78bfa; }
.dark .sh-comment { color: #9ca3af; }
.dark .sh-selector { color: #a78bfa; }
.dark .sh-value { color: #34d399; }
.dark .sh-atrule { color: #f87171; }
.dark .sh-prolog { color: #9ca3af; }
.dark .sh-entity { color: #22d3ee; }
`;
